import NumberInput from '../../../components/NumberInput';
import { useState, useEffect } from 'react';

import FrameSection from '../../../components/dnd/FrameSection';
import TableInventory from '../../../components/dnd/inventory/TableInventory';
import { useAuth } from '../../../hooks/useAuth';
import { WeightUnit } from '../../../components/dnd/inventory/Weight';

type WeaponData = {
    id: number;
    source: number;
    name: string;
    description: string;
    atk_bonus: number;
    damage: string;
    properties: string[];
    mastery: string;
    isMartial: boolean;
    isRanged: boolean;
    weight: number;
    value: { cp: number; sp: number; ep: number; gp: number; pp: number };
    isMagic: boolean;
    requiresAttunement: boolean;
};

type WeaponReference = {
    id: number;
    source: number;
    quantity: number;
    notes: string | null;
};

type EquippedWeaponData = WeaponData & WeaponReference & {
    atkBonus: number;
    equipped: boolean;
};

type CatalogResponse = {
    success: boolean;
    data: WeaponData[];
};

type AddWeaponResponse = {
    success: boolean;
    data: { id: number; quantity: number; notes: string | null };
};

type WeaponsProps = {
    characterId: number;
    items: WeaponReference[];
    onWeightChange?: (weight: number) => void;
    weightUnit: WeightUnit;
};

function Weapons({
    characterId,
    items,
    onWeightChange,
    weightUnit,
}: WeaponsProps) {
    const { apiRequest } = useAuth();
    const [weapons, setWeapons] = useState<EquippedWeaponData[]>([]);
    const [searchableItems, setSearchableItems] = useState<EquippedWeaponData[]>([]);
    const [error, setError] = useState('');

    useEffect(() => {
        onWeightChange?.(weapons.reduce((total, weapon) => total + weapon.weight * weapon.quantity, 0));
    }, [weapons, onWeightChange]);

    useEffect(() => {
        const fetchWeapons = async () => {
            const result = await apiRequest<CatalogResponse>('/api/weapons/', 'GET');
            const catalog = result.data.map((weapon) => ({
                ...weapon,
                atkBonus: weapon.atk_bonus,
                quantity: 1,
                equipped: false,
                notes: null,
            }));
            setSearchableItems(catalog);
            setWeapons(items.flatMap((reference) => {
                const weapon = catalog.find((catalogWeapon) => catalogWeapon.id === reference.id);
                return weapon ? [{ ...weapon, ...reference }] : [];
            }));
        };

        fetchWeapons();
    }, [apiRequest, items]);

    const handleSelectWeapon = async (weapon: EquippedWeaponData) => {
        try {
            await apiRequest<AddWeaponResponse>(`/api/characters/${characterId}/weapons`, 'POST', {
                weapon_id: weapon.id,
                quantity: 1,
            });
            setWeapons((currentWeapons) => {
                const existingWeapon = currentWeapons.find((item) => item.id === weapon.id);
                if (existingWeapon) {
                    return currentWeapons.map((item) => item.id === weapon.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item);
                }
                return [...currentWeapons, { ...weapon, quantity: 1, equipped: false, notes: null }];
            });
            setError('');
        } catch {
            setError('Could not add the weapon.');
        }
    };

    const handleSaveWeapon = async (weapon: EquippedWeaponData) => {
        try {
            await apiRequest(`/api/characters/${characterId}/weapons/${weapon.id}`, 'PUT', {
                quantity: weapon.quantity,
                notes: weapon.notes || null,
            });
            setError('');
            return true;
        } catch {
            setError('Could not save weapon changes.');
            return false;
        }
    };

    const handleDeleteWeapon = async (weapon: EquippedWeaponData) => {
        try {
            await apiRequest(`/api/characters/${characterId}/weapons/${weapon.id}`, 'DELETE');
            setError('');
            return true;
        } catch {
            setError('Could not delete the weapon.');
            return false;
        }
    };

    return (
        <>
            <FrameSection title="Weapons" />

            {error && <div className="alert alert-danger" role="alert">{error}</div>}

            <TableInventory
                className="inventory-table--weapons"
                thead={
                    <>
                        <th scope="col">
                            Name
                        </th>

                        <th scope="col" style={{ width: 100 }} className="text-center">
                            <i className="fas fa-hashtag" title="Quantity" aria-label="Quantity" />
                        </th>

                        <th scope="col" style={{ width: 100 }} className="text-center">
                            Atk
                        </th>

                        <th scope="col" style={{ width: 200 }}>
                            Damage &amp; Type
                        </th>

                        <th scope="col" style={{ width: 100 }} className="text-center">
                            Dmg
                        </th>

                    </>
                }
                searchableItems={searchableItems.filter((item) => !weapons.some((w) => w.id === item.id))}
                items={weapons}
                sorted
                sortedSearchable
                onSelect={handleSelectWeapon}
                onSave={handleSaveWeapon}
                onDelete={handleDeleteWeapon}
                getDescription={(item) => item.description}
                onNotesChange={(item, notes) => setWeapons(weapons.map((current) => current.id === item.id ? { ...current, notes } : current))}
                getWeight={(item) => item.weight}
                weightUnit={weightUnit}
                getValue={(item) => item.value}
                emptyMessage="You don't have any weapons."
                columnCount={5}
                mobileColumnCount={3}
                getKey={(item) => String(item.id)}
                setItems={setWeapons}
                getLabel={(item) => item.name}
                renderItem={(item, isEditing) => (
                    <>
                        <td>
                            {item.name}
                        </td>

                        <td className="text-center">
                            {isEditing ? (
                                <NumberInput
                                    min={1}
                                    value={item.quantity}
                                    onValueChange={(quantity) => {
                                        setWeapons(weapons.map((w) => (w.id === item.id ? { ...w, quantity } : w)));
                                    }}
                                    className="form-control"
                                />
                            ) : (
                                item.quantity
                            )}
                        </td>

                        <td className="text-center">
                            TODO
                        </td>

                        <td style={{ whiteSpace: 'pre-wrap' }}>
                            {item.damage}
                        </td>

                        <td className="text-center">
                            TODO
                        </td>

                    </>
                )}
                renderDetails={(item, isEditing) => (
                    <div className="d-flex flex-column gap-1">
                        {item.isMagic && (
                            <p className="mb-0"><strong>Magic Weapon</strong></p>
                        )}

                        {item.requiresAttunement && (
                            <p className="mb-0"><strong>Requires Attunement</strong></p>
                        )}

                        <p className="mb-0">
                            <strong>Type:</strong> {item.isRanged ? 'Ranged' : 'Melee'}, {item.isMartial ? 'Martial' : 'Simple'}
                        </p>

                        {item.properties.length > 0 && (
                            <p className="mb-0"><strong>Properties:</strong> {item.properties.join(', ')}</p>
                        )}

                        {item.mastery && <p className="mb-0"><strong>Mastery:</strong> {item.mastery}</p>}

                    </div>
                )}
            />
        </>
    );
}

export default Weapons;
